import React from 'react';

import { rem } from '@vimeo/bokeh';
import { CircleExclamation, GridView, ListUl } from '@vimeo/bokeh-icons';
import { ColdStorageVideoLockedIcon } from '@vimeo/content-cards';
import { GetUserProjectFields } from '@vimeo/gctl-api/fetchers/user/project';
import { translate } from '@vimeo/i18n-runtime';
import {
  DEFAULT_PRIVACY_OPTIONS,
  DEFAULT_PRIVACY_VALUES,
  VIDEO_PRIVACY_ICON_MAP,
} from '@vimeo/privacy-management';
import { getTeamPrivacyTranslation } from '@vimeo/teams';

import {
  SortType,
  LiveEventsSortType,
  ClipPrivacyTypeFilterValue,
  ContentTypeFilterValue,
} from './types';

export const CONTENT_TYPE = {
  FOLDER: 'folder',
  VIDEO: 'video',
  LIVE_EVENT: 'live_event',
  SHOWCASE: 'showcase',
} as const;

export const CLIP_PRIVACY_TYPE = {
  UNLISTED: 'unlisted',
  PASSWORD: 'password',
  HIDE_FROM_VIMEO: 'hide_from_vimeo',
  PUBLIC: 'public',
  PRIVATE: 'private',
  TEAM: 'team',
  LOCKED: 'locked',
  COLD_PRIVACY: 'cold_privacy',
} as const;

export const SORT_OPTION = {
  TITLE: 'alphabetical',
  MODIFIED: 'last_user_action_event_date',
  CREATED: 'date',
  DURATION: 'duration',
} as const;

export const SWM_SORT_OPTION = {
  TITLE: SORT_OPTION.TITLE,
  SHARED_DATE: 'shared_date',
  SHARED_USER_NAME: 'shared_user_name',
} as const;

export const SHOWCASES_SORT_OPTION = {
  TITLE: SORT_OPTION.TITLE,
  LAST_MODIFIED: 'last_modified',
  CREATED: SORT_OPTION.CREATED,
} as const;

export const RECENTLY_DELETED_SORT_OPTION = {
  TITLE: SORT_OPTION.TITLE,
  MODIFIED: SORT_OPTION.MODIFIED,
  DURATION: SORT_OPTION.DURATION,
} as const;

export const SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
} as const;

export const LAYOUT = {
  LIST: 'LIST_LAYOUT',
  GRID: 'GRID_LAYOUT',
} as const;

export const LAYOUT_CONFIG = {
  [LAYOUT.LIST]: {
    name: 'list',
    Icon: ListUl,
    tooltipText: translate('View list') as string,
  },
  [LAYOUT.GRID]: {
    name: 'grid',
    Icon: GridView,
    tooltipText: translate('View grid') as string,
  },
};

export const DEFAULT_SORT_OPTION = SORT_OPTION.MODIFIED;
export const DEFAULT_SORT_DIRECTION = SORT_DIRECTION.DESC;

export const DEFAULT_SORT: SortType = {
  type: DEFAULT_SORT_OPTION,
  direction: DEFAULT_SORT_DIRECTION,
};

export const DEFAULT_EVENTS_SORT_OPTION = SORT_OPTION.CREATED;

export const DEFAULT_EVENTS_SORT: LiveEventsSortType = {
  type: DEFAULT_EVENTS_SORT_OPTION,
  direction: DEFAULT_SORT_DIRECTION,
};

export const DEFAULT_SHOWCASES_SORT_OPTION = SORT_OPTION.CREATED;
export const DEFAULT_SHOWCASES_SORT: SortType = {
  type: DEFAULT_SHOWCASES_SORT_OPTION,
  direction: DEFAULT_SORT_DIRECTION,
};

export const DEFAULT_RECENTLY_DELETED_SORT_OPTION = RECENTLY_DELETED_SORT_OPTION.MODIFIED;
export const DEFAULT_RECENTLY_DELETED_SORT: SortType = {
  type: DEFAULT_RECENTLY_DELETED_SORT_OPTION,
  direction: DEFAULT_SORT_DIRECTION,
};

export const DEFAULT_DATE_DISPLAY = SORT_OPTION.CREATED;

export const VL_SORT_OPTIONS = [
  SORT_OPTION.TITLE,
  SORT_OPTION.MODIFIED,
  SORT_OPTION.CREATED,
  SORT_OPTION.DURATION,
];

export const VL_EVENTS_SORT_OPTIONS = [SORT_OPTION.TITLE, SORT_OPTION.CREATED];

export const VL_SWM_SORT_OPTIONS = [
  SWM_SORT_OPTION.TITLE,
  SWM_SORT_OPTION.SHARED_DATE,
  SWM_SORT_OPTION.SHARED_USER_NAME,
];

export const VL_SHOWCASES_SORT_OPTIONS = [
  SHOWCASES_SORT_OPTION.TITLE,
  SHOWCASES_SORT_OPTION.LAST_MODIFIED,
  SHOWCASES_SORT_OPTION.CREATED,
];

export const SORT_OPTIONS = {
  alphabetical_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.TITLE,
    text: translate('Title, A to Z'),
  },
  alphabetical_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.TITLE,
    text: translate('Title, Z to A'),
  },
  last_user_action_event_date_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.MODIFIED,
    text: translate('Last modified'),
  },
  last_user_action_event_date_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.MODIFIED,
    text: translate('First modified'),
  },
  date_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.CREATED,
    text: translate('Last added'),
  },
  date_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.CREATED,
    text: translate('First added'),
  },
  duration_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.DURATION,
    text: translate('Longest'),
  },
  duration_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.DURATION,
    text: translate('Shortest'),
  },
};

export const EVENTS_SORT_OPTIONS = {
  alphabetical_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.TITLE,
    text: translate('Title, A to Z'),
  },
  alphabetical_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.TITLE,
    text: translate('Title, Z to A'),
  },
  date_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.CREATED,
    text: translate('Last added'),
  },
  date_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.CREATED,
    text: translate('First added'),
  },
};

export const SHARED_WITH_ME_SORT_OPTIONS = {
  alphabetical_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SORT_OPTION.TITLE,
    text: translate('Title, A to Z'),
  },
  alphabetical_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SORT_OPTION.TITLE,
    text: translate('Title, Z to A'),
  },
  shared_date_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SWM_SORT_OPTION.SHARED_DATE,
    text: translate('Last shared'),
  },
  shared_date_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SWM_SORT_OPTION.SHARED_DATE,
    text: translate('First shared'),
  },
  shared_user_name_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SWM_SORT_OPTION.SHARED_USER_NAME,
    text: translate('Shared by, A to Z'),
  },
  shared_user_name_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SWM_SORT_OPTION.SHARED_USER_NAME,
    text: translate('Shared by, Z to A'),
  },
};

export const SHOWCASES_SORT_OPTIONS = {
  alphabetical_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SHOWCASES_SORT_OPTION.TITLE,
    text: translate('Title, A to Z'),
  },
  alphabetical_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SHOWCASES_SORT_OPTION.TITLE,
    text: translate('Title, Z to A'),
  },
  last_modified_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SHOWCASES_SORT_OPTION.LAST_MODIFIED,
    text: translate('Last modified'),
  },
  last_modified_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SHOWCASES_SORT_OPTION.LAST_MODIFIED,
    text: translate('First modified'),
  },
  date_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: SHOWCASES_SORT_OPTION.CREATED,
    text: translate('Last added'),
  },
  date_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: SHOWCASES_SORT_OPTION.CREATED,
    text: translate('First added'),
  },
};

export const RECENTLY_DELETED_SORT_OPTIONS = {
  alphabetical_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: RECENTLY_DELETED_SORT_OPTION.TITLE,
    text: translate('Title, A to Z'),
  },
  alphabetical_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: RECENTLY_DELETED_SORT_OPTION.TITLE,
    text: translate('Title, Z to A'),
  },
  last_user_action_event_date_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: RECENTLY_DELETED_SORT_OPTION.MODIFIED,
    text: translate('Latest deleted'),
  },
  last_user_action_event_date_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: RECENTLY_DELETED_SORT_OPTION.MODIFIED,
    text: translate('Earliest deleted'),
  },
  duration_desc: {
    sortDirection: SORT_DIRECTION.DESC,
    sortBy: RECENTLY_DELETED_SORT_OPTION.DURATION,
    text: translate('Longest'),
  },
  duration_asc: {
    sortDirection: SORT_DIRECTION.ASC,
    sortBy: RECENTLY_DELETED_SORT_OPTION.DURATION,
    text: translate('Shortest'),
  },
};

export const CONTENT_TYPE_FILTER_OPTIONS_BY_VALUE: Record<
  ContentTypeFilterValue,
  { label: string; value: ContentTypeFilterValue }
> = {
  all: {
    label: translate('All'),
    value: 'all',
  },
  folder: {
    label: translate('Folders'),
    value: 'folder',
  },
  showcase: {
    label: translate('Showcases'),
    value: 'showcase',
  },
  video: {
    label: translate('Videos'),
    value: 'video',
  },
};

export const getPrivacyTypeFilterOptionsByValue = (
  teamName?: string,
  isWorkspace?: boolean,
): Record<
  ClipPrivacyTypeFilterValue,
  { label: string; value: ClipPrivacyTypeFilterValue; icon?: React.ElementType }
> => {
  const filterValueByPrivacy = Object.fromEntries(
    Object.entries(DEFAULT_PRIVACY_VALUES).map(([k, v]) => [v, k.toLowerCase()]),
  ) as Record<string, ClipPrivacyTypeFilterValue>;

  return {
    ...Object.fromEntries(
      DEFAULT_PRIVACY_OPTIONS.flatMap((opt) => {
        const value = filterValueByPrivacy[opt.privacy];
        if (!value) return [];
        const node = VIDEO_PRIVACY_ICON_MAP[opt.privacy]?.icon;
        return [
          [
            value,
            {
              label:
                opt.privacy === DEFAULT_PRIVACY_VALUES.TEAM
                  ? getTeamPrivacyTranslation(teamName, isWorkspace)
                  : opt.title,
              value,
              icon: React.isValidElement(node) ? (node.type as React.ElementType) : undefined,
            },
          ],
        ];
      }),
    ),
    locked: {
      label: translate('Restricted'),
      value: 'locked',
      icon: ColdStorageVideoLockedIcon,
    },
    cold_privacy: {
      label: translate('Forced to Private'),
      value: 'cold_privacy',
      icon: CircleExclamation,
    },
  } as Record<
    ClipPrivacyTypeFilterValue,
    { label: string; value: ClipPrivacyTypeFilterValue; icon?: React.ElementType }
  >;
};

type SortEventKey =
  | keyof typeof SORT_OPTIONS
  | 'shared_date_desc'
  | 'shared_date_asc'
  | 'shared_user_name_asc'
  | 'shared_user_name_desc';

export const BP_EVENT_COPY: Record<SortEventKey, string> = {
  alphabetical_asc: 'title, a to z',
  alphabetical_desc: 'title, z to a',
  last_user_action_event_date_desc: 'last modified',
  last_user_action_event_date_asc: 'first modified',
  date_desc: 'last added',
  date_asc: 'first added',
  duration_desc: 'longest',
  duration_asc: 'shortest',
  shared_date_desc: 'last shared',
  shared_date_asc: 'first shared',
  shared_user_name_asc: 'shared by, a to z',
  shared_user_name_desc: 'shared by, z to a',
};

export const VL_SORT_LOCAL_STORAGE_KEY = 'vl_sort';
export const VL_DATE_LOCAL_STORAGE_KEY = 'vl_date';
/*
 * Events needs to have a separate storage key, since not
 * all sorts are valid on the Events page
 */
export const VL_EVENTS_SORT_LOCAL_STORAGE_KEY = 'vl_events_sort';
/*
 * Shared with me needs to have a separate storage key, since not
 * all sorts are valid on the SWM page
 */
export const VL_SWM_SORT_LOCAL_STORAGE_KEY = 'vl_swm_sort';
export const VL_SHOWCASES_SORT_LOCAL_STORAGE_KEY = 'vl_showcases_sort';

export const HIDE_PRIVACY_COLUMN_BREAKPOINT = rem(1280);
export const HIDE_FILE_SIZE_COLUMN_BREAKPOINT = rem(1152);
export const HIDE_DATE_COLUMN_BREAKPOINT = rem(1024);

export const PAGE = {
  MVV: 'mvv',
  ELIHP: 'elihp',
  SHOWCASES: 'library_showcases',
  VLS: 'vls',
  SEARCH: 'search_result_page',
  LIBRARY: 'library',
  EVENTS: 'events',
  SVV: 'svv',
  UNKNOWN: '',
  SHARED_WITH_ME: 'shared_with_me',
} as const;

export const Path = {
  AccountSettings: '/settings/account/general',
  Analytics: '/analytics',
  Home: '/home',
  LiveEvents: '/library/events',
  ManageTeam: '/settings/account/team_members',
  ManageVideos: '/manage/videos',
  MVV: '/user/[userId]/folder/[folderId]',
  SharedWithMe: '/shared-with-me',
  Showcases: '/library/showcases',
  EventSeries: '/library/event_series',
  TeamLibrary: '/library',
  Watch: '/watch',
  IntegrationsCenter: '/integrations-center/[[...slug]]',
  RecentlyDeleted: '/recently-deleted',
} as const;

export const CONTENT_CARD_TITLE_MAX_WIDTH = rem(300);

// the top nav has a z-index of 1001 and we want our actions menus over the top nav
export const ACTIONS_MENU_Z_INDEX = 1002;

export const MAX_SELECTION_FOR_BULK_MOVE = 50;
export const MAX_SELECTION_FOR_BULK_PRIVACY = 50;
export const MAX_SELECTION_FOR_BULK_ADD_TO_SHOWCASES = 100;

export const USER_PREFERENCE_ID = {
  PREF_ORGANIZATION_UUID: 'orguid',
  PREF_WORKSPACE_UUID: 'wspuid',
} as const;

export const SHARED_WITH_ME_DIR_NAME = translate('Shared with me');
export const GET_USER_PROJECT_API_FIELDS: GetUserProjectFields = [
  'metadata.connections.ancestorPath',
];

export const PRIVACY_TO_LABEL: Record<string, string> = {
  anybody: translate('Public'),
  disable: translate('Hide from Vimeo'),
  nobody: translate('Private'),
  password: translate('Password'),
  unlisted: translate('Unlisted'),
  ptv: translate('VOD'),
  ptvhide: translate('VOD'),
  contacts: translate('People I follow'),
  users: translate('People I choose'),
  purgatory: translate('Locked'),
};
